import React, { useState, useEffect } from 'react';
import { db, auth, provider } from '../config/firebaseConfig';
import { signInWithPopup, signOut } from 'firebase/auth';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';

const VideoEntryForm = () => {
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [videoFormData, setVideoFormData] = useState({
    id: '',
    title: '',
    videoId: '',
    category: '',
  });
  const [categoryFormData, setCategoryFormData] = useState({ name: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Kullanıcı durumunu kontrol et
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Videoları ve kategorileri Firestore'dan çek
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Videolar
        const videosSnapshot = await getDocs(collection(db, 'videos'));
        const videosList = videosSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVideos(videosList);

        // Kategoriler
        const categoriesSnapshot = await getDocs(collection(db, 'categories'));
        const categoriesList = categoriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoriesList);
      } catch (err) {
        setError('Veriler yüklenemedi: ' + err.message);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Google ile giriş
  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError('Giriş başarısız: ' + err.message);
    }
  };

  // Çıkış yap
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      setError('Çıkış başarısız: ' + err.message);
    }
  };

  // Video formu: input değişimi
  const handleVideoInputChange = (e) => {
    const { name, value } = e.target;
    setVideoFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Kategori formu: input değişimi
  const handleCategoryInputChange = (e) => {
    setCategoryFormData({ name: e.target.value });
  };

  // Video ekleme/düzenleme
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!videoFormData.title || !videoFormData.videoId || !videoFormData.category) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      if (isEditing) {
        // Düzenleme
        const videoRef = doc(db, 'videos', videoFormData.id);
        await updateDoc(videoRef, {
          title: videoFormData.title,
          videoId: videoFormData.videoId,
          category: videoFormData.category,
        });
        setSuccess('Video başarıyla güncellendi!');
      } else {
        // Ekleme
        await addDoc(collection(db, 'videos'), {
          title: videoFormData.title,
          videoId: videoFormData.videoId,
          category: videoFormData.category,
        });
        setSuccess('Video başarıyla eklendi!');
      }

      // Formu sıfırla
      setVideoFormData({ id: '', title: '', videoId: '', category: '' });
      setIsEditing(false);

      // Videoları yeniden çek
      const videosSnapshot = await getDocs(collection(db, 'videos'));
      setVideos(videosSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setError('İşlem başarısız: ' + err.message);
    }
  };

  // Video silme
  const handleDelete = async (id) => {
    if (!window.confirm('Bu videoyu silmek istediğinize emin misiniz?')) return;

    try {
      await deleteDoc(doc(db, 'videos', id));
      setSuccess('Video başarıyla silindi!');
      setVideos(videos.filter((video) => video.id !== id));
    } catch (err) {
      setError('Silme başarısız: ' + err.message);
    }
  };

  // Video düzenleme için formu doldur
  const handleEdit = (video) => {
    setVideoFormData({
      id: video.id,
      title: video.title,
      videoId: video.videoId,
      category: video.category,
    });
    setIsEditing(true);
  };

  // Kategori ekleme
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!categoryFormData.name) {
      setError('Lütfen kategori adını girin.');
      return;
    }

    try {
      await addDoc(collection(db, 'categories'), {
        name: categoryFormData.name,
      });
      setSuccess('Kategori başarıyla eklendi!');
      setCategoryFormData({ name: '' });

      // Kategorileri yeniden çek
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      setCategories(categoriesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setError('Kategori eklenemedi: ' + err.message);
    }
  };

  // Giriş yapılmamışsa giriş ekranı
  if (!user) {
    return (
      <div className="vw-admin-container py-5">
        <h2 className="vw-admin-form-title text-center mb-4">Admin Paneli - Giriş</h2>
        <div className="vw-admin-form-section p-4">
          <div className="text-center">
            <p className="vw-admin-no-results">Video ve kategori yönetmek için giriş yapın.</p>
            <button className="vw-admin-auth-btn btn" onClick={handleSignIn}>
              Google ile Giriş Yap
            </button>
            {error && <p className="vw-admin-error-message mt-3">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Giriş yapılmışsa yönetim paneli
  return (
    <div className="vw-admin-container py-5">
      <h2 className="vw-admin-form-title text-center mb-4">Video Yönetim Paneli</h2>
      <div className="vw-admin-form-section p-4 mb-4">
        <div className="d-flex justify-content-end mb-3">
          <button className="vw-admin-auth-btn btn" onClick={handleSignOut}>
            Çıkış Yap ({user.email})
          </button>
        </div>

        {/* Video Ekleme/Düzenleme Formu */}
        <h4 className="vw-admin-sub-title mb-3">{isEditing ? 'Video Düzenle' : 'Yeni Video Ekle'}</h4>
        <form onSubmit={handleVideoSubmit}>
          <div className="mb-3">
            <label htmlFor="title" className="vw-admin-form-label form-label">
              Video Başlığı
            </label>
            <input
              type="text"
              id="title"
              name="title"
              className="vw-admin-form-input form-control"
              value={videoFormData.title}
              onChange={handleVideoInputChange}
              placeholder="Video başlığını girin"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="videoId" className="vw-admin-form-label form-label">
              YouTube Video ID
            </label>
            <input
              type="text"
              id="videoId"
              name="videoId"
              className="vw-admin-form-input form-control"
              value={videoFormData.videoId}
              onChange={handleVideoInputChange}
              placeholder="YouTube video ID'sini girin (örn. dDiJkz1bH-Y)"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="category" className="vw-admin-form-label form-label">
              Kategori
            </label>
            <select
              id="category"
              name="category"
              className="vw-admin-form-select form-select"
              value={videoFormData.category}
              onChange={handleVideoInputChange}
            >
              <option value="">Kategori seçin</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="text-center">
            <button type="submit" className="vw-admin-submit-btn btn me-2">
              {isEditing ? 'Güncelle' : 'Ekle'}
            </button>
            {isEditing && (
              <button
                type="button"
                className="vw-admin-cancel-btn btn"
                onClick={() => {
                  setVideoFormData({ id: '', title: '', videoId: '', category: '' });
                  setIsEditing(false);
                }}
              >
                İptal
              </button>
            )}
          </div>
        </form>

        {/* Kategori Ekleme Formu */}
        <h4 className="vw-admin-sub-title mt-5 mb-3">Yeni Kategori Ekle</h4>
        <form onSubmit={handleCategorySubmit}>
          <div className="mb-3">
            <label htmlFor="categoryName" className="vw-admin-form-label form-label">
              Kategori Adı
            </label>
            <input
              type="text"
              id="categoryName"
              name="name"
              className="vw-admin-form-input form-control"
              value={categoryFormData.name}
              onChange={handleCategoryInputChange}
              placeholder="Kategori adını girin"
            />
          </div>
          <div className="text-center">
            <button type="submit" className="vw-admin-submit-btn btn">
              Kategori Ekle
            </button>
          </div>
        </form>

        {error && <p className="vw-admin-error-message mt-3 text-center">{error}</p>}
        {success && <p className="vw-admin-success-message mt-3 text-center">{success}</p>}
      </div>

      {/* Video Listesi */}
      <h4 className="vw-admin-sub-title mb-3">Mevcut Videolar</h4>
      <div className="vw-admin-form-section p-4">
        {videos.length > 0 ? (
          <ul className="list-group">
            {videos.map((video) => (
              <li key={video.id} className="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <strong>{video.title}</strong> ({video.category})
                  <br />
                  <small>YouTube ID: {video.videoId}</small>
                </div>
                <div>
                  <button
                    className="vw-admin-edit-btn btn btn-sm me-2"
                    onClick={() => handleEdit(video)}
                  >
                    Düzenle
                  </button>
                  <button
                    className="vw-admin-delete-btn btn btn-sm"
                    onClick={() => handleDelete(video.id)}
                  >
                    Sil
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="vw-admin-no-results text-center">Henüz video eklenmemiş.</p>
        )}
      </div>
    </div>
  );
};

export default VideoEntryForm;